import { useId, type ReactNode } from "react";

/**
 * Form alanı sarmalayıcısı: etiket + ipucu + hata + içerik.
 *
 * NİÇİN: eski panelde etiketler `<label for>` bağı olmadan yazılıyordu; ekran
 * okuyucu alanın adını söyleyemiyor, etikete tıklamak alanı odaklamıyordu.
 * Buradaki `useId()` bağı her alanda otomatik kurar.
 */
export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label: string;
  /** Alanın altında görünen açıklama. */
  hint?: string;
  /** Doğrulama hatası — varsa ipucunun yerini alır ve kırmızı gösterilir. */
  error?: string | null;
  required?: boolean;
  /** İçerideki girdinin id'si. Verilmezse otomatik üretilir. */
  htmlFor?: string;
  children: ReactNode;
}) {
  const autoId = useId();
  const id = htmlFor ?? autoId;
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;

  /**
   * ★ `htmlFor` VERİLMEDİYSE ETİKET İÇERİĞİ SARAR (örtük etiketleme).
   *
   * Eski davranış her durumda `<label htmlFor={id}>` basıyordu. `htmlFor`
   * geçilmediğinde `id` yalnızca `useId()`den üretilmiş bir dizeydi ve BÖYLE
   * BİR ÖĞE YOKTU — içerideki girdinin kendi id'si (ya da hiç id'si) vardı.
   * Sonuç: ekran okuyucu alanın adını söyleyemiyor, etikete tıklamak alanı
   * odaklamıyordu. Kullanımların çoğunda `htmlFor` geçilmiyor.
   *
   * Örtük etiketleme HTML'in kendi çözümü: `<label>` içindeki ilk
   * etiketlenebilir öğeye kendiliğinden bağlanır ve içerideki bileşenin id
   * ALMASINI GEREKTİRMEZ. `cloneElement` ile id enjekte etmek daha temiz
   * görünürdü ama `DecimalInput`/`MoneyInput` gibi sarmalayıcıların prop
   * arayüzü kapalı; bilmedikleri prop'u sessizce yutar ve bağ yine kurulmazdı —
   * görünürde düzelmiş, gerçekte bozuk bir sonuç.
   *
   * ⚠ KALAN SINIR: `aria-describedby` hâlâ sarmalayıcıda, girdinin kendisinde
   * değil; ipucu/hata odaklanınca okunmaz. Tam çözüm girdinin id almasını,
   * yani çağıranın `htmlFor` geçmesini gerektirir — o durumda aşağıdaki açık
   * dal zaten tam doğru davranır.
   */
  const etiketMetni = (
    <>
      {label}
      {required ? (
        <span className="ml-0.5 text-red-600" aria-hidden="true">
          *
        </span>
      ) : null}
    </>
  );

  return (
    <div className="min-w-0">
      {htmlFor ? (
        <>
          <label htmlFor={htmlFor} className="label">
            {etiketMetni}
          </label>
          <div className="mt-1" aria-describedby={describedBy}>
            {children}
          </div>
        </>
      ) : (
        <label className="block">
          <span className="label">{etiketMetni}</span>
          <span className="mt-1 block" aria-describedby={describedBy}>
            {children}
          </span>
        </label>
      )}

      {error ? (
        <p id={`${id}-err`} role="alert" className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-sm text-gray-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default Field;
